// ============================================================
// TMR Portal Admin — o hook que toda lista usa para exportar
// ============================================================
import { useCallback, useRef, useState } from 'react';
import { toCsv } from './csv';
import { downloadBlob } from './download';
import { exportFileName } from './fileName';
import { fetchAllPages } from './fetchAllPages';
import { toXlsxBlob } from './xlsx';
import type { ExportFormat, ExportProgress, ExportSpec } from './types';

/** What the export is doing right now. */
export type ExportState = 'idle' | 'running' | 'error';

/** What {@link useListExport} hands back to the screen. */
export interface ListExport {
  state: ExportState;
  /** Rows read so far and rows expected — what the button counts up. Null before the first page. */
  progress: ExportProgress | null;
  /** Why the last attempt failed; null unless `state` is `'error'`. */
  error: Error | null;
  /** Starts an export. Calling it while one runs is ignored rather than queued. */
  run: (format: ExportFormat) => Promise<void>;
  /** Clears an error, so the menu goes back to offering the two formats. */
  reset: () => void;
}

/**
 * Exports a list the operator is looking at (20 §3).
 *
 * Generic on purpose: every list in the portal is the same problem — walk the filtered result set, project
 * each row into columns, serialize, download — and the only thing that differs is the columns and where
 * a page comes from. Both arrive in the {@link ExportSpec}.
 *
 * **The file is built here rather than on the server**, and that is what makes the spreadsheet say what
 * the screen says: the headers, the CNPJ mask and the PT-BR word for every status are already in the
 * frontend, and a second copy in the backend would drift from this one on the first change made to only
 * one of them (01 §16).
 *
 * @param spec What to export: the file's name, its columns, and how to read one page.
 */
export function useListExport<T>(spec: ExportSpec<T>): ListExport {
  const [state, setState] = useState<ExportState>('idle');
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // The spec is rebuilt on every render (its `fetchPage` closes over the screen's filters), so it is read
  // through a ref: `run` must not change identity on every keystroke in the search box, and it must use
  // the filters as they stand when it is called, not as they stood when it was created.
  const latest = useRef(spec);
  latest.current = spec;

  const running = useRef(false);

  const run = useCallback(async (format: ExportFormat) => {
    // Ignored rather than queued: a second click is somebody who thinks nothing happened, and starting a
    // second walk would double the requests to say the same thing.
    if (running.current) return;

    running.current = true;
    setState('running');
    setProgress(null);
    setError(null);

    try {
      const { fileName, columns, fetchPage } = latest.current;
      const rows = await fetchAllPages(fetchPage, setProgress);

      const blob = format === 'csv'
        ? new Blob([toCsv(rows, columns)], { type: 'text/csv;charset=utf-8' })
        : await toXlsxBlob(rows, columns);

      downloadBlob(blob, exportFileName(fileName, format));
      setState('idle');
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error('Não foi possível exportar a lista.'));
      setState('error');
    } finally {
      running.current = false;
    }
  }, []);

  const reset = useCallback(() => {
    setState('idle');
    setError(null);
    setProgress(null);
  }, []);

  return { state, progress, error, run, reset };
}
